import { reactive, readonly } from 'vue';

const STORAGE_KEY = 'cookie_consent';

const CATEGORIES = {
  necessary: true,
  analytics: false,
  marketing: false,
  externalMedia: false,
};

const state = reactive({
  consent: { ...CATEGORIES },
  decided: false,
  timestamp: null,
});

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (data && typeof data.consent === 'object' && data.timestamp) {
      state.consent = { ...CATEGORIES, ...data.consent, necessary: true };
      state.decided = true;
      state.timestamp = data.timestamp;
      return true;
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return false;
}

function saveToStorage() {
  const data = {
    consent: { ...state.consent },
    timestamp: new Date().toISOString(),
    version: 1,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  state.timestamp = data.timestamp;
}

function hasConsent(category) {
  if (category === 'necessary') return true;
  return !!state.consent[category];
}

function setConsent(categories) {
  state.consent = {
    necessary: true,
    analytics: !!categories.analytics,
    marketing: !!categories.marketing,
    externalMedia: !!categories.externalMedia,
  };
  state.decided = true;
  saveToStorage();
  applyConsent();
}

function acceptAll() {
  setConsent({ analytics: true, marketing: true, externalMedia: true });
}

function rejectAll() {
  setConsent({ analytics: false, marketing: false, externalMedia: false });
}

function resetConsent() {
  localStorage.removeItem(STORAGE_KEY);
  state.consent = { ...CATEGORIES };
  state.decided = false;
  state.timestamp = null;
}

function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    Object.entries(attrs).forEach(([key, val]) => script.setAttribute(key, val));
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function loadHubSpot() {
  return loadScript('//js-eu1.hs-scripts.com/145900210.js', { id: 'hs-script-loader' });
}

function loadFacebookPixel() {
  if (window.fbq) return;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', '1029576409259161');
  window.fbq('track', 'PageView');
  /* eslint-enable */
}

function loadGoogleAds() {
  return loadScript('https://www.googletagmanager.com/gtag/js?id=AW-17324924941').then(() => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', 'AW-17324924941');
  });
}

function applyConsent() {
  if (hasConsent('marketing')) {
    loadHubSpot();
    loadFacebookPixel();
    loadGoogleAds();
  }
}

const alreadyLoaded = loadFromStorage();
if (alreadyLoaded) {
  applyConsent();
}

export function useConsentManager() {
  return {
    state: readonly(state),
    hasConsent,
    setConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    applyConsent,
  };
}
